import {
  AccentIcon,
  AccentIconShapeEnum,
  AccentIconSizeEnum,
  AccentVariantEnum,
} from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Divider } from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { IconText } from '@delmonicos/shared-design-system-react/src/components/icon-text/icon-text';
import {
  IconColor,
  IconSize,
} from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
  TextFontSize,
  TextWeight,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { computeTreePhaseVoltage } from '@delmonicos/shared-sdk/libs/core/src/helpers/compute-phase-voltage';
import { getGenericPowerType } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/power-type.helper';
import {
  ConnectorFormat,
  EvseStatus,
  PowerType,
} from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/enums/ocpi-protocol-enums';
import { ConnectorModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpi/src/ocpi-cpo-api/models/locations/connector.model';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

// TODO: move mapTypeToGenericConnectorType to a shared helper
import { mapTypeToGenericConnectorType } from '@app/feature/charge-point/helper/connector-type.helper';
import { useEmspBoLocationContext } from '@app/feature/emsp-bo-location/emsp-bo-location.context';

import * as css from './location-evses-card.module.scss';

const mapEvseStatusToAccentVariant = (
  status: EvseStatus,
): AccentVariantEnum => {
  switch (status) {
    case EvseStatus.AVAILABLE:
      return AccentVariantEnum.Available;
    case EvseStatus.CHARGING:
      return AccentVariantEnum.Charging;
    case EvseStatus.RESERVED:
      return AccentVariantEnum.Primary;
    case EvseStatus.BLOCKED:
    case EvseStatus.INOPERATIVE:
    case EvseStatus.OUTOFORDER:
      return AccentVariantEnum.Faulted;
    default:
      return AccentVariantEnum.Unavailable;
  }
};

interface ConnectorRowProps {
  connector: ConnectorModel;
  statusVariant: AccentVariantEnum;
}

const ConnectorRow = ({
  connector,
  statusVariant,
}: ConnectorRowProps): React.JSX.Element => {
  const { t } = useTranslation();

  const connectorType = mapTypeToGenericConnectorType(connector.standard);
  const powerType = getGenericPowerType(connector.power_type);

  const realVoltage =
    connector.power_type === PowerType.AC_3_PHASE
      ? computeTreePhaseVoltage(Number(connector.voltage))
      : connector.voltage;

  const power =
    realVoltage && connector.amperage
      ? ((connector.amperage * realVoltage) / 1000).toFixed(1)
      : '?';

  return (
    <Stack direction="column" gap={GapEnum.S}>
      <AccentTitle
        icon={connectorType.icon}
        iconShape={AccentIconShapeEnum.Round}
        variant={statusVariant}
        text={t(
          `delmonicos-shared-sdk.ocpi-protocol-enums.connector-type-alt.${connectorType.name}`,
        )}
        as={HeadingEnum.H4}
      />
      <Stack alignItems="center" flex={1} justifyContent="space-between">
        <IconText
          iconName={`power-${powerType.toLowerCase()}`}
          iconProps={{ color: IconColor.Secondary, iconSize: IconSize.Small }}
          text={t(
            `delmonicos-shared-sdk.ocpi-protocol-enums.power-type-alt.${powerType}`,
          )}
          textProps={{
            fontWeight: TextWeight.Regular,
            fontSize: TextFontSize.Medium,
            color: TextColor.Secondary,
          }}
        />
        <IconText
          iconName="lightning-02"
          iconProps={{ color: IconColor.Secondary, iconSize: IconSize.Small }}
          text={`${power} kW`}
          textProps={{
            fontWeight: TextWeight.Regular,
            fontSize: TextFontSize.Medium,
            color: TextColor.Secondary,
          }}
        />
        <IconText
          iconName="lightning-01"
          iconProps={{ color: IconColor.Secondary, iconSize: IconSize.Small }}
          text={`${connector.amperage} A`}
          textProps={{
            fontWeight: TextWeight.Regular,
            fontSize: TextFontSize.Medium,
            color: TextColor.Secondary,
          }}
        />
        <AccentIcon
          shape={AccentIconShapeEnum.Round}
          variant={AccentVariantEnum.Surface}
          size={AccentIconSizeEnum.ExtraSmall}
          icon={connector.format === ConnectorFormat.CABLE ? 'wire' : 'no-wire'}
        />
      </Stack>
    </Stack>
  );
};

export const LocationEvsesCardComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const location = useEmspBoLocationContext();
  const evses = location.evses ?? [];

  return (
    <Card flex={1} direction="column" gap={GapEnum.L}>
      <AccentTitle
        icon="electric-terminal-3"
        text={t('$.emsp-bo-location-details.evses-card.title')}
        as={HeadingEnum.H2}
        variant={AccentVariantEnum.Accent3}
      />
      {evses.length === 0 ? (
        <Text color={TextColor.Secondary}>
          {t('$.emsp-bo-location-details.evses-card.empty')}
        </Text>
      ) : (
        evses.map((evse) => {
          const statusVariant = mapEvseStatusToAccentVariant(evse.status);

          return (
            <Stack key={evse.uid} direction="column" gap={GapEnum.M}>
              <Card gap={GapEnum.S} alignItems="stretch" direction="row">
                <Card.FullHeight
                  className={classNames(css.evseStatus, css[statusVariant])}
                />
                <Stack
                  direction="column"
                  gap={GapEnum.M}
                  flex={1}
                  alignItems="stretch">
                  <Text
                    fontWeight={TextWeight.Medium}
                    fontSize={TextFontSize.H3}>
                    {evse.evse_id ?? evse.uid}
                  </Text>
                  {evse.connectors.map((connector, connectorIndex) => (
                    <Stack
                      key={connector.id}
                      direction="column"
                      gap={GapEnum.S}>
                      {connectorIndex > 0 && <Divider />}
                      <ConnectorRow
                        connector={connector}
                        statusVariant={statusVariant}
                      />
                    </Stack>
                  ))}
                </Stack>
              </Card>
            </Stack>
          );
        })
      )}
    </Card>
  );
};
